import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

export function isGuardedInternalLinkClick(event: MouseEvent): HTMLAnchorElement | null {
  if (event.defaultPrevented || event.button !== 0) {
    return null;
  }
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
    return null;
  }

  const target = event.target;
  if (!(target instanceof Element)) {
    return null;
  }

  const anchor = target.closest('a[href]');
  if (!(anchor instanceof HTMLAnchorElement)) {
    return null;
  }
  if (anchor.target === '_blank' || anchor.hasAttribute('download')) {
    return null;
  }

  const url = new URL(anchor.href, window.location.href);
  if (url.origin !== window.location.origin) {
    return null;
  }
  if (
    url.pathname === window.location.pathname
    && url.search === window.location.search
    && url.hash === window.location.hash
  ) {
    return null;
  }

  return anchor;
}

export function useUnsavedChangesGuard(isDirty: boolean) {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const requestNavigation = useCallback((action: () => void) => {
    if (!isDirty) {
      action();
      return;
    }
    pendingActionRef.current = action;
    setShowDialog(true);
  }, [isDirty]);

  const confirmDiscard = useCallback(() => {
    const action = pendingActionRef.current;
    pendingActionRef.current = null;
    setShowDialog(false);
    action?.();
  }, []);

  const cancelDiscard = useCallback(() => {
    pendingActionRef.current = null;
    setShowDialog(false);
  }, []);

  useEffect(() => {
    if (!isDirty) {
      pendingActionRef.current = null;
      setShowDialog(false);
    }
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const onBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', onBeforeUnload);
    return () => window.removeEventListener('beforeunload', onBeforeUnload);
  }, [isDirty]);

  useEffect(() => {
    if (!isDirty) {
      return undefined;
    }

    const onClick = (event: MouseEvent) => {
      const anchor = isGuardedInternalLinkClick(event);
      if (!anchor) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();

      const url = new URL(anchor.href, window.location.href);
      requestNavigation(() => {
        navigate(`${url.pathname}${url.search}${url.hash}`);
      });
    };

    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, [isDirty, navigate, requestNavigation]);

  return {
    showDialog,
    requestNavigation,
    confirmDiscard,
    cancelDiscard,
  };
}
